import React, { useState } from 'react';
import { Plus, Settings2, Shield, Users } from 'lucide-react';
import PillTabs from '../../../components/common/PillTabs';
import IdentityProviderSection from './AccessManagement/identityProviders/IdentityProviderSection';
import UsersDirectoryPanel from './AccessManagement/UsersDirectoryPanel';
import RolesPermissionsPanel from './AccessManagement/RolesPermissionsPanel';
import AccessIdentityOverview from './AccessManagement/AccessIdentityOverview';
import { useAccessTheme } from './AccessManagement/useAccessTheme';
import { logAccess } from './AccessManagement/accessSettingsLogger';
import {
  mockRoutes,
  type RoutePrivilege,
} from './Access/routePrivilegeTemplates';

export type { RoutePrivilege };
export { mockRoutes };

type AccessTab = 'users' | 'roles' | 'identity';

const TABS: { id: AccessTab; label: string; icon: typeof Users }[] = [
  { id: 'users', label: 'Users', icon: Users },
  { id: 'roles', label: 'Roles & Permissions', icon: Shield },
  { id: 'identity', label: 'Identity Providers', icon: Settings2 },
];

const AccessSettings = () => {
  const t = useAccessTheme();
  const [tab, setTab] = useState<AccessTab>('users');
  const [openAddUser, setOpenAddUser] = useState(false);
  const [openAddRole, setOpenAddRole] = useState(false);
  const [showAddProviderModal, setShowAddProviderModal] = useState(false);
  const [focusAdmins, setFocusAdmins] = useState(false);

  const selectTab = (next: AccessTab, admins = false) => {
    setTab(next);
    setFocusAdmins(next === 'users' && admins);
    setOpenAddUser(false);
    setOpenAddRole(false);
    setShowAddProviderModal(false);
    logAccess('app', 'tab_changed', { tab: next, focusAdmins: next === 'users' && admins });
  };

  return (
    <div className={`w-full min-w-0 -m-3 min-h-[calc(100vh-1.5rem)] ${t.isLight ? 'bg-slate-50' : 'bg-neutral-950'}`}>
      <div className="px-6 py-5 space-y-5">
        <AccessIdentityOverview
          onManageUsers={() => selectTab('users', false)}
          onViewAdmins={() => selectTab('users', true)}
          onManageRoles={() => selectTab('roles')}
          onSetupSso={() => selectTab('identity')}
          tabs={
            <PillTabs
              isLight={t.isLight}
              ariaLabel="Access settings sections"
              value={tab}
              items={TABS}
              onChange={(id) => selectTab(id, false)}
            />
          }
        />

        {tab === 'users' ? (
          <UsersDirectoryPanel
            openAddUser={openAddUser}
            onOpenAddUserChange={setOpenAddUser}
            focusAdmins={focusAdmins}
            onClearAdminFocus={() => setFocusAdmins(false)}
          />
        ) : null}
        {tab === 'roles' ? (
          <RolesPermissionsPanel openAddRole={openAddRole} onOpenAddRoleChange={setOpenAddRole} />
        ) : null}
        {tab === 'identity' ? (
          <div className="space-y-3">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowAddProviderModal(true);
                  logAccess('app', 'add_identity_provider_opened');
                }}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg shadow-sm ${t.btnPrimary}`}
              >
                <Plus className="w-4 h-4" /> Identity Provider
              </button>
            </div>
            <IdentityProviderSection
              showHeader={false}
              addModalOpen={showAddProviderModal}
              onOpenAddModal={() => setShowAddProviderModal(true)}
              onCloseAddModal={() => setShowAddProviderModal(false)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AccessSettings;
